import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Package, MapPin, Phone, Mail, User, Store, Hash, ShoppingBag,
  CheckCircle2, CircleDashed, Loader2, ArrowLeft, FileImage,
  Flag, Navigation, AlertCircle,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * v2.7.58+E35 · DELIVERY SYSTEM · Section 4
 *
 * Driver's Sequential Stop Checklist page. Once a manifest is IN_PROGRESS
 * the driver is locked into this view: check each stop off in order, view
 * the customer's uploaded receipt inline, then hit "Mark Delivered" when
 * every stop is picked up. Section 5 will add auto-payout on that final
 * transition.
 */
export default function DeliveryActivePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // stop_index of active checkin
  const [notes, setNotes] = useState({}); // { [stop_index]: "note text" }
  const [receiptUrls, setReceiptUrls] = useState({}); // { [stop_index]: objectUrl }
  const [finalBusy, setFinalBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/deliveries/mine/${id}`);
      setDelivery(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load delivery");
      nav("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  const stops = useMemo(
    () => (delivery?.stops || []).slice().sort((a, b) => a.index - b.index),
    [delivery],
  );
  const doneCount   = stops.filter(s => s.picked_up_at).length;
  const allDone     = stops.length > 0 && doneCount === stops.length;
  const currentIdx  = useMemo(
    () => stops.find(s => !s.picked_up_at)?.index ?? stops.length,
    [stops],
  );

  const captureGps = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({ lat: null, lng: null });
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve({ lat: null, lng: null }),
        { enableHighAccuracy: true, timeout: 6000, maximumAge: 30000 },
      );
    });

  const checkin = async (stopIndex) => {
    setBusy(stopIndex);
    try {
      const { lat, lng } = await captureGps();
      const payload = { note: (notes[stopIndex] || "").trim() || null };
      if (lat != null && lng != null) { payload.lat = lat; payload.lng = lng; }
      await api.post(`/deliveries/mine/${id}/stops/${stopIndex}/checkin`, payload);
      toast.success(`Stop ${stopIndex + 1} picked up`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not check in");
    } finally {
      setBusy(null);
    }
  };

  const viewReceipt = async (stopIndex) => {
    if (receiptUrls[stopIndex]) {
      window.open(receiptUrls[stopIndex], "_blank");
      return;
    }
    try {
      const res = await api.get(
        `/deliveries/mine/${id}/receipt/${stopIndex}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      setReceiptUrls(prev => ({ ...prev, [stopIndex]: url }));
      window.open(url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Receipt unavailable");
    }
  };

  const openMap = (address) => {
    const q = encodeURIComponent(address);
    window.open(`https://maps.google.com/maps?q=${q}`, "_blank");
  };

  const markDelivered = async () => {
    if (!allDone) {
      toast.error("Every stop must be picked up before marking delivered.");
      return;
    }
    if (!window.confirm("Mark this delivery as DELIVERED? This closes out the manifest.")) return;
    setFinalBusy(true);
    try {
      await api.post(`/deliveries/mine/${id}/deliver`);
      toast.success("Delivery closed out — great work!");
      nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark delivered");
    } finally {
      setFinalBusy(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Active Delivery">
        <div className="flex items-center justify-center py-16 text-zinc-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2"/>Loading manifest…
        </div>
      </Layout>
    );
  }
  if (!delivery) return null;

  return (
    <Layout title="Active Delivery" back={false}>
      <Button
        onClick={() => nav("/dashboard")}
        variant="ghost"
        className="mb-3 text-zinc-400 hover:text-zinc-200"
        data-testid="delivery-back-btn"
      >
        <ArrowLeft className="h-4 w-4 mr-1"/>Back to Dashboard
      </Button>

      {/* Header — customer + drop-off */}
      <Card className="p-4 mb-4 bg-zinc-900 border-zinc-800" data-testid="delivery-header">
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-xl bg-emerald-400/15 border border-emerald-400/40 flex items-center justify-center text-emerald-300 flex-shrink-0">
            <Package className="h-6 w-6"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded bg-emerald-400 text-zinc-950">
                {delivery.status}
              </span>
              <span className="text-[10px] text-zinc-500 font-mono">#{delivery.id.slice(0, 8)}</span>
            </div>
            <div className="mt-1 text-lg font-extrabold text-zinc-100 flex items-center gap-2">
              <User className="h-4 w-4 text-zinc-500"/>{delivery.customer_name}
            </div>
            <div className="mt-0.5 text-xs text-zinc-400 flex items-center gap-3 flex-wrap">
              <a href={`tel:${delivery.customer_phone}`} className="flex items-center gap-1 hover:text-zinc-100">
                <Phone className="h-3.5 w-3.5"/>{delivery.customer_phone}
              </a>
              <a href={`mailto:${delivery.customer_email}`} className="flex items-center gap-1 hover:text-zinc-100">
                <Mail className="h-3.5 w-3.5"/>{delivery.customer_email}
              </a>
            </div>
            <button
              type="button"
              onClick={() => openMap(delivery.dropoff_address)}
              className="mt-2 text-xs bg-zinc-950 border border-zinc-700 rounded px-3 py-1.5 hover:border-emerald-400/50 flex items-center gap-1.5 w-full sm:w-auto"
              data-testid="dropoff-map-btn"
            >
              <MapPin className="h-3.5 w-3.5 text-emerald-300"/>
              <span className="text-zinc-300">Final drop-off ·</span>
              <span className="text-zinc-100 font-medium truncate">{delivery.dropoff_address}</span>
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4">
          <div className="flex justify-between text-[11px] text-zinc-400 mb-1.5">
            <span>Stops picked up</span>
            <span className="font-bold text-zinc-200">{doneCount} / {stops.length}</span>
          </div>
          <div className="h-2 bg-zinc-800 rounded overflow-hidden">
            <div
              className="h-full bg-emerald-400 transition-all duration-300"
              style={{ width: `${stops.length ? (doneCount / stops.length) * 100 : 0}%` }}
              data-testid="delivery-progress-bar"
            />
          </div>
        </div>
      </Card>

      {/* Sequential stop checklist */}
      <div className="space-y-3">
        {stops.map((s) => {
          const done       = !!s.picked_up_at;
          const isCurrent  = !done && s.index === currentIdx;
          const isLocked   = !done && s.index > currentIdx;
          return (
            <Card
              key={s.index}
              data-testid={`stop-card-${s.index}`}
              className={
                done
                  ? "p-4 bg-emerald-500/5 border-emerald-400/30"
                  : isCurrent
                    ? "p-4 bg-zinc-900 border-2 border-amber-400/60 ring-2 ring-amber-400/20"
                    : "p-4 bg-zinc-950 border-zinc-800 opacity-60"
              }
            >
              <div className="flex items-start gap-3">
                <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 font-extrabold ${
                  done ? "bg-emerald-400 text-zinc-950"
                    : isCurrent ? "bg-amber-400 text-zinc-950"
                    : "bg-zinc-800 text-zinc-500"
                }`}>
                  {done ? <CheckCircle2 className="h-5 w-5"/> : (s.index + 1)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-extrabold uppercase tracking-widest ${
                      done ? "text-emerald-300" : isCurrent ? "text-amber-300" : "text-zinc-500"
                    }`}>
                      Stop {s.index + 1} of {stops.length}
                      {done && " · picked up"}
                      {isCurrent && " · next up"}
                      {isLocked && " · locked"}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-base font-bold text-zinc-100">
                    <Store className="h-4 w-4 text-zinc-500"/>{s.business_name}
                  </div>
                  <button
                    type="button"
                    onClick={() => openMap(s.business_address)}
                    className="mt-1.5 text-xs bg-zinc-950 border border-zinc-700 rounded px-3 py-1.5 hover:border-amber-400/50 flex items-center gap-1.5 w-full text-left"
                    data-testid={`stop-map-btn-${s.index}`}
                  >
                    <Navigation className="h-3.5 w-3.5 text-amber-300 flex-shrink-0"/>
                    <span className="text-zinc-300 truncate">{s.business_address}</span>
                  </button>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                    <div className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5">
                      <div className="text-zinc-500 flex items-center gap-1"><Hash className="h-3 w-3"/>Order #</div>
                      <div className="font-mono font-bold text-zinc-200 mt-0.5">{s.order_number || "—"}</div>
                    </div>
                    <div className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5">
                      <div className="text-zinc-500 flex items-center gap-1"><ShoppingBag className="h-3 w-3"/>Items</div>
                      <div className="font-mono font-bold text-zinc-200 mt-0.5">{s.item_count}</div>
                    </div>
                  </div>

                  {s.receipt_upload_id && (
                    <button
                      type="button"
                      onClick={() => viewReceipt(s.index)}
                      className="mt-2 text-[11px] font-bold text-amber-300 hover:text-amber-200 flex items-center gap-1"
                      data-testid={`view-receipt-${s.index}`}
                    >
                      <FileImage className="h-3.5 w-3.5"/>View customer receipt
                      <span className="text-zinc-500 font-normal">({s.receipt_filename})</span>
                    </button>
                  )}

                  {done && s.picked_up_note && (
                    <div className="mt-2 text-[11px] bg-emerald-500/5 border border-emerald-400/20 rounded px-2 py-1.5 text-emerald-200">
                      Note · {s.picked_up_note}
                    </div>
                  )}
                  {done && s.picked_up_at && (
                    <div className="mt-1.5 text-[10px] text-emerald-300">
                      ✓ Checked in {new Date(s.picked_up_at).toLocaleString()}
                    </div>
                  )}

                  {isCurrent && (
                    <div className="mt-3 space-y-2">
                      <Textarea
                        placeholder="Optional note (e.g. items missing, substitution accepted…)"
                        value={notes[s.index] || ""}
                        onChange={(e) => setNotes(prev => ({ ...prev, [s.index]: e.target.value }))}
                        rows={2}
                        className="bg-zinc-950 border-zinc-700 text-xs"
                        data-testid={`stop-note-${s.index}`}
                      />
                      <Button
                        onClick={() => checkin(s.index)}
                        disabled={busy === s.index}
                        data-testid={`checkin-btn-${s.index}`}
                        className="w-full bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold tracking-wider"
                      >
                        {busy === s.index
                          ? <><Loader2 className="h-4 w-4 animate-spin mr-1"/>Checking in…</>
                          : <><CheckCircle2 className="h-4 w-4 mr-1"/>Check in Stop {s.index + 1}</>}
                      </Button>
                    </div>
                  )}
                  {isLocked && (
                    <div className="mt-2 text-[11px] text-zinc-500 flex items-center gap-1">
                      <CircleDashed className="h-3.5 w-3.5"/>
                      Complete stop {currentIdx + 1} first — this is a sequential checklist.
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Final Deliver action */}
      <Card className={`mt-5 p-5 ${allDone
        ? "bg-emerald-500/10 border-2 border-emerald-400/60"
        : "bg-zinc-950 border-zinc-800"}`} data-testid="mark-delivered-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${
              allDone ? "bg-emerald-400 text-zinc-950" : "bg-zinc-800 text-zinc-500"
            }`}>
              <Flag className="h-5 w-5"/>
            </div>
            <div>
              <div className="font-extrabold text-zinc-100">Final Drop-Off</div>
              <div className="text-xs text-zinc-400">
                {allDone
                  ? "All stops picked up — deliver to the customer and close out."
                  : `Pick up the remaining ${stops.length - doneCount} stop${stops.length - doneCount === 1 ? "" : "s"} to unlock.`}
              </div>
            </div>
          </div>
          <Button
            onClick={markDelivered}
            disabled={!allDone || finalBusy}
            data-testid="mark-delivered-btn"
            className="bg-emerald-400 hover:bg-emerald-300 text-zinc-950 font-extrabold tracking-widest disabled:bg-zinc-800 disabled:text-zinc-500"
          >
            {finalBusy
              ? <><Loader2 className="h-4 w-4 animate-spin mr-1"/>Closing…</>
              : <><Flag className="h-4 w-4 mr-1"/>Mark Delivered</>}
          </Button>
        </div>
        {!allDone && (
          <div className="mt-3 text-[11px] text-zinc-500 flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5"/>
            Every stop on the manifest must be checked in before the final drop-off can be logged.
          </div>
        )}
      </Card>
    </Layout>
  );
}
